import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  PageContainer,
  PageHeader,
  Card,
  Badge,
  Button,
  FormGroup,
  Input,
  AlertBanner,
  EmptyState,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface OrderLine {
  id: string;
  product_name: string;
  format_name: string;
  qty: number;
  unit_price: number;
  line_total: number;
}

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  order_type: "pickup" | "delivery";
  status: "pending" | "confirmed" | "preparing" | "ready" | "completed" | "cancelled";
  items: OrderLine[];
  total: number;
  minutes_waiting: number;
  pickup_time: string | null;
  created_at: string;
  cancel_reason?: string;
}

interface OnlineSettings {
  accepting_orders: boolean;
  pickup_lead_time: number;
  delivery_enabled: boolean;
  min_order_amount: number;
  announcement: string;
}

// ============================================================
// STATUS HELPERS
// ============================================================

const STATUS_CONFIG: Record<string, { label: string; bg: string; border: string; text: string; badgeVariant: "gold" | "blue" | "default" | "green" }> = {
  pending:   { label: "Pending",   bg: "#F6BE0018", border: "#F6BE0050", text: "var(--rbw-gold-dark)", badgeVariant: "gold" },
  confirmed: { label: "Confirmed", bg: "#8BB8E818", border: "#8BB8E850", text: "var(--rbw-blue-dark)", badgeVariant: "blue" },
  preparing: { label: "Preparing", bg: "rgba(51,63,72,0.08)", border: "rgba(51,63,72,0.25)", text: "var(--rbw-navy)", badgeVariant: "default" },
  ready:     { label: "Ready",     bg: "#2D8A4E18", border: "#2D8A4E50", text: "var(--rbw-success)",  badgeVariant: "green" },
};

const NEXT_STATUS: Record<string, string> = {
  pending: "confirmed",
  confirmed: "preparing",
  preparing: "ready",
  ready: "completed",
};

const ACTION_LABELS: Record<string, string> = {
  pending: "Confirm",
  confirmed: "Start Preparing",
  preparing: "Mark Ready",
  ready: "Complete",
};

// ============================================================
// ORDER CARD
// ============================================================

function OrderCard({
  order,
  onAdvance,
  onCancel,
}: {
  order: Order;
  onAdvance: (id: string, status: string) => void;
  onCancel: (id: string) => void;
}) {
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const nextStatus = NEXT_STATUS[order.status];
  const actionLabel = ACTION_LABELS[order.status];
  const urgent = order.minutes_waiting > 15;

  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-3"
      style={{
        background: cfg.bg,
        border: `2px solid ${cfg.border}`,
        boxShadow: urgent ? "0 0 0 2px var(--rbw-danger)" : "0 1px 4px rgba(51,63,72,0.06)",
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
              #{order.order_number}
            </span>
            <Badge variant={order.order_type === "delivery" ? "purple" : "blue"}>
              {order.order_type === "delivery" ? "Delivery" : "Pickup"}
            </Badge>
          </div>
          <div className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
            {order.customer_name}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <Badge variant={cfg.badgeVariant}>{cfg.label}</Badge>
          <div
            className="text-xs font-semibold mt-1"
            style={{ color: urgent ? "var(--rbw-danger)" : "var(--rbw-gray-dark)" }}
          >
            {order.minutes_waiting}m ago
          </div>
        </div>
      </div>

      {/* Pickup time */}
      {order.pickup_time && (
        <div className="text-xs font-medium" style={{ color: "var(--rbw-blue-dark)" }}>
          Pickup at {new Date(order.pickup_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
        </div>
      )}

      {/* Items */}
      <div className="flex flex-col gap-1">
        {order.items.map((line) => (
          <div key={line.id} className="flex justify-between text-sm">
            <span style={{ color: "var(--rbw-navy)" }}>
              <span className="font-semibold">{line.qty}x</span> {line.product_name}
              {line.format_name && (
                <span style={{ color: "var(--rbw-gray-dark)" }}> - {line.format_name}</span>
              )}
            </span>
            <span className="font-medium" style={{ color: "var(--rbw-navy)" }}>
              ${Number(line.line_total).toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      {/* Total */}
      <div
        className="flex justify-between text-sm font-bold pt-2"
        style={{ borderTop: "1px solid rgba(51,63,72,0.1)", color: "var(--rbw-navy)" }}
      >
        <span>Total</span>
        <span>${Number(order.total).toFixed(2)}</span>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-1">
        {nextStatus && (
          <Button
            variant={order.status === "ready" ? "gold" : "secondary"}
            size="sm"
            className="flex-1"
            onClick={() => onAdvance(order.id, nextStatus)}
          >
            {actionLabel}
          </Button>
        )}
        <Button variant="danger" size="sm" onClick={() => onCancel(order.id)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// SETTINGS PANEL
// ============================================================

function SettingsPanel({
  settings,
  onSave,
  isOwner,
}: {
  settings: OnlineSettings;
  onSave: (s: OnlineSettings) => void;
  isOwner: boolean;
}) {
  const [local, setLocal] = useState<OnlineSettings>(settings);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setLocal(settings);
    setDirty(false);
  }, [settings]);

  const update = (patch: Partial<OnlineSettings>) => {
    setLocal((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  return (
    <Card>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Accepting orders toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => update({ accepting_orders: !local.accepting_orders })}
            disabled={!isOwner}
            className="relative w-12 h-7 rounded-full transition-colors"
            style={{
              background: local.accepting_orders ? "var(--rbw-success)" : "var(--rbw-gray)",
              opacity: isOwner ? 1 : 0.5,
            }}
          >
            <span
              className="absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform shadow"
              style={{ transform: local.accepting_orders ? "translateX(20px)" : "translateX(0)" }}
            />
          </button>
          <span className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
            {local.accepting_orders ? "Accepting Orders" : "Orders Paused"}
          </span>
        </div>

        {/* Delivery toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => update({ delivery_enabled: !local.delivery_enabled })}
            disabled={!isOwner}
            className="relative w-12 h-7 rounded-full transition-colors"
            style={{
              background: local.delivery_enabled ? "var(--rbw-success)" : "var(--rbw-gray)",
              opacity: isOwner ? 1 : 0.5,
            }}
          >
            <span
              className="absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform shadow"
              style={{ transform: local.delivery_enabled ? "translateX(20px)" : "translateX(0)" }}
            />
          </button>
          <span className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
            Delivery {local.delivery_enabled ? "Enabled" : "Disabled"}
          </span>
        </div>

        <FormGroup label="Pickup Lead Time (min)">
          <Input
            type="number"
            value={local.pickup_lead_time}
            onChange={(v) => update({ pickup_lead_time: Number(v) })}
            min="0"
          />
        </FormGroup>

        <FormGroup label="Min Order Amount ($)">
          <Input
            type="number"
            value={local.min_order_amount}
            onChange={(v) => update({ min_order_amount: Number(v) })}
            min="0"
            step="0.01"
          />
        </FormGroup>

        <FormGroup label="Announcement" span={2}>
          <Input
            value={local.announcement}
            onChange={(v) => update({ announcement: v })}
            placeholder="e.g. Closed for holiday Monday"
          />
        </FormGroup>
      </div>

      {dirty && isOwner && (
        <div className="flex justify-end mt-4">
          <Button variant="primary" size="sm" onClick={() => onSave(local)}>
            Save Settings
          </Button>
        </div>
      )}
      {!isOwner && (
        <p className="text-xs mt-3" style={{ color: "var(--rbw-gray-dark)" }}>
          Only owners can change online order settings.
        </p>
      )}
    </Card>
  );
}

// ============================================================
// CANCEL MODAL
// ============================================================

function CancelModal({
  onConfirm,
  onClose,
}: {
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl p-6 w-full max-w-md"
        style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>
          Cancel Order
        </h3>
        <FormGroup label="Reason (optional)">
          <Input
            value={reason}
            onChange={setReason}
            placeholder="Out of stock, customer request, etc."
          />
        </FormGroup>
        <div className="flex gap-3 mt-5 justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Go Back
          </Button>
          <Button variant="danger" size="sm" onClick={() => onConfirm(reason)}>
            Cancel Order
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================

export default function OrderQueuePage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [settings, setSettings] = useState<OnlineSettings | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const isOwner = user?.role === "owner";

  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get("/online-orders/queue");
      setOrders(res.data);
      setLastRefresh(new Date());
    } catch {
      // silently retry on next interval
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get("/online-orders/settings");
      setSettings(res.data);
    } catch {
      // settings not critical for queue display
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchSettings();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchOrders, fetchSettings]);

  const advanceStatus = async (id: string, status: string) => {
    try {
      setError("");
      await api.put(`/online-orders/${id}/status`, { status });
      await fetchOrders();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to update status";
      setError(msg);
    }
  };

  const cancelOrder = async (reason: string) => {
    if (!cancelId) return;
    try {
      setError("");
      await api.put(`/online-orders/${cancelId}/status`, { status: "cancelled", cancel_reason: reason });
      setCancelId(null);
      await fetchOrders();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to cancel order";
      setError(msg);
    }
  };

  const saveSettings = async (s: OnlineSettings) => {
    try {
      setError("");
      await api.put("/online-orders/settings", s);
      setSettings(s);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save settings";
      setError(msg);
    }
  };

  // Group by status columns
  const columns: { key: string; label: string }[] = [
    { key: "pending", label: "Pending" },
    { key: "confirmed", label: "Confirmed" },
    { key: "preparing", label: "Preparing" },
    { key: "ready", label: "Ready for Pickup" },
  ];

  const grouped: Record<string, Order[]> = { pending: [], confirmed: [], preparing: [], ready: [] };
  orders.forEach((o) => {
    if (grouped[o.status]) grouped[o.status].push(o);
  });

  const totalActive = orders.length;

  return (
    <PageContainer wide>
      <PageHeader
        title="Order Queue"
        subtitle={`${totalActive} active order${totalActive !== 1 ? "s" : ""} | Last refreshed ${lastRefresh.toLocaleTimeString()}`}
        action={
          <div className="flex items-center gap-3">
            {settings && !settings.accepting_orders && (
              <Badge variant="danger">Orders Paused</Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
            >
              {showSettings ? "Hide Settings" : "Settings"}
            </Button>
            <Button variant="secondary" size="sm" onClick={fetchOrders}>
              Refresh
            </Button>
          </div>
        }
      />

      {error && <AlertBanner variant="danger">{error}</AlertBanner>}

      {/* Settings panel */}
      {showSettings && settings && (
        <div className="mb-6">
          <SettingsPanel settings={settings} onSave={saveSettings} isOwner={isOwner} />
        </div>
      )}

      {/* Order columns */}
      {totalActive === 0 ? (
        <Card>
          <EmptyState
            icon="&#128230;"
            message="No active orders"
            detail="New online orders will appear here automatically"
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {columns.map((col) => {
            const colCfg = STATUS_CONFIG[col.key];
            return (
              <div key={col.key}>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ background: colCfg.border }}
                  />
                  <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: colCfg.text }}>
                    {col.label}
                  </h3>
                  <span
                    className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: colCfg.bg, color: colCfg.text }}
                  >
                    {grouped[col.key].length}
                  </span>
                </div>
                <div className="flex flex-col gap-3">
                  {grouped[col.key].map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      onAdvance={advanceStatus}
                      onCancel={(id) => setCancelId(id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cancel modal */}
      {cancelId && (
        <CancelModal
          onConfirm={cancelOrder}
          onClose={() => setCancelId(null)}
        />
      )}
    </PageContainer>
  );
}
